/**
 * SuitStatement — builds a structured output record for the MiFID suitability
 * statement document, based on new contact.cgProfiling.* profile keys.
 *
 * Input  : Contact, a flat ProfileParams map (supports both full
 *          "contact.cgProfiling.<key>" and short "<key>" access), and an
 *          optional orders array (same shape as AutoSuitStatement).
 * Output : SuitStatementData — one flat record with all document fields.
 *
 * Fields marked null are placeholders for data sourced from portfolio/price
 * data that is not yet wired up; they can be filled in later.
 */

import type { Contact } from "../../api/types";
import type { SuitOrderInput } from "./AutoSuitStatement";

// ---------------------------------------------------------------------------
// Output type
// ---------------------------------------------------------------------------

export type SuitStatementData = {
  // -- Document references -------------------------------------------------
  /** Reference to signed MiFID customer profile document. */
  MIFIDProfileDocRef: string | null;
  /** Reference to signed investor profile document. */
  InvestorProfileDocRef: string | null;
  /** Reference to signed order / offer document. */
  OrderDocRef: string | null;

  // -- Customer ------------------------------------------------------------
  /** Full name of the customer. */
  ClientName: string | null;
  /** Customer classification text (e.g. "Ikke-profesjonell"). */
  ClientClassText: string | null;
  /** Earliest need for significant withdrawal. */
  tidligsteUttak: string | null;
  /** Main investment objective. */
  hovedmal: string | null;
  /** Comfort with portfolio volatility (Lav / Moderat / Høy / Offensiv). */
  riskkomfort: string | null;
  /** Liquidity need (Høyt / Moderat / Lavt / Svært lavt). */
  withdraw: string | null;
  /** Classified ability to bear losses. */
  fallcons: string | null;

  // -- Investor profile — instruments --------------------------------------
  know_funds: string | null;
  exp_funds: string | null;
  freq_funds: string | null;
  period_funds: string | null;
  know_listed_eq: string | null;
  exp_listed_eq: string | null;
  freq_listed_eq: string | null;
  period_listed_eq: string | null;
  know_unlisted_eq: string | null;
  exp_unlisted_eq: string | null;
  freq_unlisted_eq: string | null;
  period_unlisted_eq: string | null;
  know_bonds: string | null;
  exp_bonds: string | null;
  freq_bonds: string | null;
  period_bonds: string | null;
  know_complex: string | null;
  exp_complex: string | null;
  freq_complex: string | null;
  period_complex: string | null;
  /** "Ja" / "Nei" — whether the customer has relevant finance background. */
  finance_background_flag: string | null;
  /** Free-text description of relevant experience / education. */
  finance_background_text: string | null;

  // -- Financial bands -----------------------------------------------------
  NetWorthBandText: string | null;
  LiquidFundsBandText: string | null;
  GrossIncomeBandText: string | null;
  LiquidityBufferBandText: string | null;
  DebtBurdenText: string | null;
  IncomeStabilityText: string | null;

  // -- ESG -----------------------------------------------------------------
  ESG: string | null;
  ESGText: string | null;
  /** "ingen" | "opplyst" */
  ESGType: string | null;
  ESGPreferenceType: string | null;
  ESGPreferenceText: string | null;

  // -- Recommended equity range --------------------------------------------
  /** Recommended equity band, e.g. "25-45 %". */
  MaxEquityBandText: string | null;
  /** Maximum equity allowed, e.g. "75 %". */
  MaxEquityText: string | null;
  /** Current equity share % before orders (0–100). */
  currentEquitySharePct: number | null;
  /** Maximum allowed equity % (from risk tolerance). */
  maxEquitySharePct: number | null;
  /** Minimum recommended equity % (from risk tolerance band). */
  minRecEquitySharePct: number | null;
  /** Maximum recommended equity % (from risk tolerance band). */
  maxRecEquitySharePct: number | null;
  /** Recommended equity band text, e.g. "25-45 %". */
  RecEquityBandText: string | null;

  // -- Calculated aggregates -----------------------------------------------
  /** Aggregated knowledge level: Lav / Middels / Høy. */
  know: string | null;
  /** Aggregated experience level: Ingen / Noe / Betydelig. */
  exp: string | null;

  // -- Portfolio / recommendation ------------------------------------------
  /** Total recommended investment amount (NOK). */
  investmentAmount: number | null;
  /** Equity fraction after order execution (0–1). */
  port_stock_pct: number | null;
  /** Equity share in queued orders only, excluding the customer's existing portfolios (0–1). */
  orderStockAllocation: number | null;
  /** Fixed-income fraction after order execution (0–1). */
  port_bond_pct: number | null;
  LockupText: string | null;
  AllocationListText: string | null;
  CreditRiskFlag: boolean | null;
  Portfolio_SRRI: number | null;
  Portfolio_MRHPText: string | null;
  LiquidityClass: "LIQUID" | "PARTLY_ILLIQUID" | null;
  SettlementsDaysText: string | null;
  IlliquidPartTimeText: string | null;
  EntryFeePct: number | null;
  MgmtFeePct: number | null;
  PerfFeePct: number | null;
  OngoingCostsPct: number | null;

  // -- Metadata ------------------------------------------------------------
  AdvisorComment: string | null;
  AdvisorName: string | null;
  GeneratedDateTime: string | null;

  // -- Calculated flags ----------------------------------------------------
  /** Equity fraction is within the recommended band. */
  EquityWithin: boolean | null;
  /** Equity fraction is below the recommended band. */
  EquityUnder: boolean | null;
  /** Equity fraction is above the recommended band. */
  EquityOver: boolean | null;
  /** Overall suitability conclusion. */
  Suitable: boolean | null;
  FinancialBandsAvailable: boolean | null;
  Portfolio_SRRI_Available: boolean | null;
  MRHPMismatch: boolean | null;
  LiquidityMismatch: boolean | null;
  AllocationListAvailable: boolean | null;
  /** True when all required profile fields are present. */
  DataCompletenessFlag: boolean | null;
  /** True when investment size is significant relative to declared net worth. */
  InvestmentvsNetWorthFlag: boolean | null;

  // -- Block text fields (resolved document paragraphs) -------------------
  /** Conclusion block: equity is within recommended band (EquityWithin = true). */
  BLK_CONCL_WITHIN: string | null;
  /** Conclusion block: equity is below recommended band (EquityUnder = true). */
  BLK_CONCL_UNDER: string | null;
  /** Conclusion block: equity is above recommended band (EquityOver = true). Not suitable. */
  BLK_CONCL_OVER: string | null;
  /** Scope block: always set — lists the factors used in the suitability assessment. */
  BLK_CONCL_SCOPE: string | null;
  /** Equity justification: equity is within recommended band (EquityWithin = true). */
  BLK_EQUITY_WITHIN: string | null;
  /** Equity justification: equity is below recommended band (EquityUnder = true). */
  BLK_EQUITY_UNDER: string | null;
  /** Equity justification: equity is above recommended band (EquityOver = true). */
  BLK_EQUITY_OVER: string | null;
  /** ESG block: registered ESG preference description. */
  BLK_ESG_PREFERENCE: string | null;
  /** ESG block: no ESG factors considered. */
  BLK_ESG_NONE: string | null;
  /** ESG block: ESG preferences considered. */
  BLK_ESG_YES: string | null;
  /** Liquidity block: fully liquid assets. */
  BLK_LIQ_LIQUID: string | null;
  /** Liquidity block: partly illiquid assets. */
  BLK_LIQ_PARTLY_ILLIQUID: string | null;
  /** Liquidity block: warning about potential liquidity issues. */
  BLK_LIQ_WARN: string | null;
  /** Risk note: credit risk warning when bond portion contains corporate/high-yield bonds. */
  BLK_RISK_NOTE_CREDIT: string | null;
  /** Knowledge and experience block: base level. */
  BLK_KNOWEXP_BASE: string | null;
  /** Knowledge and experience block: low level. */
  BLK_KNOWEXP_LOW: string | null;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** mifidValueDeclineImpact value → human-readable label. */
const VALUE_DECLINE_IMPACT_LABELS: Record<string, string> = {
  "1": "Liten",
  "2": "Moderat",
  "3": "Stor",
  "4": "Svært stor",
  Lav: "Lav",
  Høy: "Høy",
  "Svært høy": "Svært høy",
};

/** mifidEarliestWithdrawal value → human-readable label (INVESTMENT_HORIZON_OPTIONS). */
const INVESTMENT_HORIZON_LABELS: Record<string, string> = {
  "1": "Under ett år",
  "2": "Fra 1–5 år",
  "3": "Mer enn 5 år",
  "5": "Mer enn 15 år / Generasjonsforvaltning",
};

/** mifidInvestmentHorizon value (org) → human-readable label (ORG_INVESTMENT_HORIZON_OPTIONS). */
const ORG_INVESTMENT_HORIZON_LABELS: Record<string, string> = {
  "1": "Under ett år",
  "2": "Fra 1–5 år",
  "3": "5–10 år",
  "4": "Mer enn 10 år",
};

/** mifidMainInvestmentPurpose value → human-readable label (INVESTMENT_GOAL_OPTIONS). */
const INVESTMENT_GOAL_LABELS: Record<string, string> = {
  "1": "Kapitalbevaring",
  "2": "Stabil avkastning",
  "3": "Moderat vekst",
  "4": "Langsiktig vekst",
};

/** mifidLiquidityNeed value → human-readable label (LIQUIDITY_NEED_OPTIONS). */
const LIQUIDITY_NEED_LABELS: Record<string, string> = {
  "1": "Svært viktig",
  "2": "Viktig",
  "3": "Lite viktig",
  "4": "Ikke viktig",
};

/** mifidIncomeStability value → human-readable label (INCOME_STABILITY_OPTIONS). */
const INCOME_STABILITY_LABELS: Record<string, string> = {
  "1": "Helt stabil",
  "2": "Noe variabel (pluss/minus 20 %)",
  "3": "Ganske variabel (pluss/minus 50 %)",
  "5": "Helt variabel",
};

/** mifidEsgPreference value → human-readable label (ESG_PREF_OPTIONS). */
const ESG_PREF_LABELS: Record<string, string> = {
  Ja: "Ja",
  Nei: "Nei, jeg følger Capguards generelle vurderinger på dette området",
};

/** mifidEsgPreferenceType value → human-readable label (ESG_PREF_TYPE_OPTIONS). */
const ESG_PREF_TYPE_LABELS: Record<string, string> = {
  "1": "Preferanse 1 – Bærekraftige investeringer (SFDR art. 8/9)",
  "2": "Preferanse 2 – EU-taksonomi-tilpassede investeringer",
  "3": "Preferanse 3 – Hensyn til negative bærekraftsvirkninger (PAI)",
  "4": "Preferanse 4 – Kombinasjon av preferansene over",
};

/** ESG preference text for document sentences (shorter variants). */
const ESG_TEXT_VARIANTS: Record<string, string> = {
  Nei: "Capguards generelle vurderinger på bærekraft",
  "1": "bærekraftige investeringer (SFDR art. 8/9)",
  "2": "EU-taksonomi-tilpassede investeringer",
  "3": "hensyn til negative bærekraftsvirkninger (PAI)",
  "4": "kombinasjon av bærekraftspreferansene",
};

function formatNOK(amount: number): string {
  return `NOK ${new Intl.NumberFormat("no-NO").format(Math.round(amount))}`;
}

/**
 * Reads a profile value by short key or full cgProfiling key.
 * Callers may pass a map built by buildProfileMap (which stores both).
 */
function pv(
  p: Record<string, string | number | boolean | null | undefined>,
  shortKey: string
): string {
  const full = `contact.cgProfiling.${shortKey}`;
  const val = p[shortKey] ?? p[full] ?? null;
  return val == null ? "" : String(val);
}

/**
 * Lookup table: (Komfort med svingninger) × (Reaksjon ved 30% fall)
 * → Endelig risikotoleranse
 */
function resolveRiskTolerance(
  riskComfort: string,
  marketDrop: string
): string | null {
  if (!riskComfort) return null;
  // Normalize numeric values stored by older form versions
  const numToLabel: Record<string, string> = {
    "1": "Lav",
    "2": "Moderat",
    "3": "Høy",
    "4": "Offensiv",
  };
  const comfort = numToLabel[riskComfort] ?? riskComfort;
  // If marketDrop is missing, use comfort directly.
  if (!marketDrop) return comfort;
  type Row = readonly [string, string, string];
  const table: Row[] = [
    ["Lav", "Selge", "Lav"],
    ["Lav", "Vente", "Lav"],
    ["Lav", "Kjøpe mer / rebalansere", "Moderat"],
    ["Moderat", "Selge", "Lav"],
    ["Moderat", "Vente", "Moderat"],
    ["Moderat", "Kjøpe mer / rebalansere", "Høy"],
    ["Høy", "Selge", "Moderat"],
    ["Høy", "Vente", "Høy"],
    ["Høy", "Kjøpe mer / rebalansere", "Offensiv"],
    ["Offensiv", "Selge", "Høy"],
    ["Offensiv", "Vente", "Offensiv"],
    ["Offensiv", "Kjøpe mer / rebalansere", "Offensiv"],
  ];
  const row = table.find(
    ([c, d]) =>
      c.toLowerCase() === comfort.toLowerCase() &&
      d.toLowerCase() === marketDrop.toLowerCase()
  );
  // If the combination isn't found in the table, fall back to comfort.
  return row ? row[2] : comfort;
}

/** Maps 5-instrument average score (1–3) to a label. */
function aggregateKnowledge(avg: number): string {
  if (avg < 1.5) return "Lav";
  if (avg < 2.5) return "Middels";
  return "Høy";
}

function aggregateExperience(avg: number): string {
  if (avg < 1.5) return "Ingen";
  if (avg < 2.5) return "Noe";
  return "Betydelig";
}

function numericAvg(values: string[]): number | null {
  const nums = values.map((v) => parseInt(v, 10)).filter((n) => !isNaN(n));
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

/** Reads a numeric value from the profile map. Returns null if absent or empty. */
function profileNum(
  profile: Record<string, string | number | boolean | null | undefined>,
  key: string
): number | null {
  const v = profile[key];
  return v !== null && v !== undefined && v !== "" ? Number(v) : null;
}

/**
 * Computes the final equity fraction (0–1) after all orders are applied.
 *
 * When `totalAUMnok` is provided (total AUM across ALL portfolios in NOK),
 * non-ordered portfolios are included in the calculation, giving the true
 * combined equity fraction for the customer.  Without it the calculation
 * falls back to only the ordered portfolios (original behaviour).
 *
 * @param orders       Order inputs (may be empty).
 * @param initFraction Current equity fraction before orders (0–1).
 * @param totalAUMnok  Total portfolio AUM in NOK before orders (optional).
 */
function computeFinStockPart(
  orders: SuitOrderInput[],
  initFraction: number,
  totalAUMnok?: number | null
): number {
  if (orders.length === 0) return initFraction;

  // When we know the total AUM we can properly account for portfolios that are
  // NOT part of any order (they keep their current stock fraction unchanged).
  if (totalAUMnok != null && totalAUMnok > 0) {
    let totalStockValue = initFraction * totalAUMnok;
    let totalPortfolioValue = totalAUMnok;

    for (const order of orders) {
      const oldBaseValue = order.sumInsValue; // portfolio value before this order
      const newBaseValue =
        order.sumInsValue +
        order.amountToInvest +
        order.oneOffAmount -
        order.withdrawal;

      if (order.kind === "model") {
        // Approximate old stock in this portfolio using the aggregate initFraction.
        const oldStockValue = oldBaseValue * initFraction;
        const newStockValue = newBaseValue * order.stockFraction;
        totalStockValue += newStockValue - oldStockValue;
        totalPortfolioValue += newBaseValue - oldBaseValue;
      } else {
        const oldStockValue = oldBaseValue * initFraction;
        let buyStockDelta = 0.0;
        let buyTotal = 0.0;
        for (const buy of order.buyOrders) {
          if (buy.isStock) buyStockDelta += buy.amountNok;
          buyTotal += buy.amountNok;
        }
        let sellStockDelta = 0.0;
        let sellTotal = 0.0;
        for (const sell of order.sellOrders) {
          const sellValue = parseFloat(sell.sellQty) * sell.marketUnitPrice;
          if (sell.isStock) sellStockDelta += sellValue;
          sellTotal += sellValue;
        }
        const newStockValue = oldStockValue + buyStockDelta - sellStockDelta;
        totalStockValue += newStockValue - oldStockValue;
        totalPortfolioValue +=
          newBaseValue + buyTotal - sellTotal - oldBaseValue;
      }
    }

    if (totalPortfolioValue <= 0) return 0.0;
    return Math.min(totalStockValue / totalPortfolioValue, 1.0);
  }

  // Fallback: only consider ordered portfolios (original algorithm).
  let totalStockValue = 0.0;
  let totalPortfolioValue = 0.0;

  for (const order of orders) {
    const baseValue =
      order.sumInsValue +
      order.amountToInvest +
      order.oneOffAmount -
      order.withdrawal;

    if (order.kind === "model") {
      totalStockValue += baseValue * order.stockFraction;
      totalPortfolioValue += baseValue;
    } else {
      const currentStockValue = baseValue * initFraction;

      let buyStockDelta = 0.0;
      let buyTotal = 0.0;
      for (const buy of order.buyOrders) {
        if (buy.isStock) buyStockDelta += buy.amountNok;
        buyTotal += buy.amountNok;
      }

      let sellStockDelta = 0.0;
      let sellTotal = 0.0;
      for (const sell of order.sellOrders) {
        const sellValue = parseFloat(sell.sellQty) * sell.marketUnitPrice;
        if (sell.isStock) sellStockDelta += sellValue;
        sellTotal += sellValue;
      }

      totalStockValue += currentStockValue + buyStockDelta - sellStockDelta;
      totalPortfolioValue += baseValue + buyTotal - sellTotal;
    }
  }

  if (totalPortfolioValue === 0) return 0.0;
  return Math.min(totalStockValue / totalPortfolioValue, 1.0);
}

// ---------------------------------------------------------------------------
// Shared types used across private methods
// ---------------------------------------------------------------------------

type PFn = (key: string) => string;
type PMap = Record<string, string | number | boolean | null | undefined>;
type StockPartOverride =
  | {
      current: number;
      fin: number;
      investmentAmount?: number | null;
    }
  | null
  | undefined;

// ---------------------------------------------------------------------------
// Class
// ---------------------------------------------------------------------------

export class SuitStatement {
  // =========================================================================
  // PRIVATE: Portfolio equity fractions
  //
  // Priority: UI test-parameter override > pre-computed stockPartOverride
  //           > profile key (contact.profiling.aumStocks)
  // =========================================================================
  private computeEquityParts(
    p: PFn,
    profile: PMap,
    orders: SuitOrderInput[],
    stockPartOverride: StockPartOverride
  ): {
    initFraction: number | null;
    finStockPart: number | null;
    finBondPart: number | null;
  } {
    let finStockPart: number | null = null;
    let initFraction: number | null = null;

    const overrideCurrentStr = p("overrideCurrentEquityPct");
    if (overrideCurrentStr !== "") {
      initFraction = parseFloat(overrideCurrentStr) / 100;
    } else if (stockPartOverride != null) {
      initFraction = stockPartOverride.current;
      finStockPart = stockPartOverride.fin;
    } else {
      const aumStocksPct =
        (profile["contact.profiling.aumStocks"] as number) ?? null;
      initFraction =
        aumStocksPct !== null ? Math.min(aumStocksPct / 100, 1.0) : null;
    }

    if (finStockPart === null && initFraction !== null) {
      const totalAUMnok =
        (profile["contact.profiling.invSumport"] as number) ?? null;
      finStockPart = computeFinStockPart(orders, initFraction, totalAUMnok);
    }

    const finBondPart =
      finStockPart !== null ? Math.max(1.0 - finStockPart, 0.0) : null;
    return { initFraction, finStockPart, finBondPart };
  }

  // =========================================================================
  // PRIVATE: Equity share percentage numeric fields
  //
  // Priority: test-parameter override > profile key (recMinStockShare etc.)
  // =========================================================================
  private resolveEquitySharePcts(
    p: PFn,
    profile: PMap
  ): {
    maxEquitySharePct: number | null;
    minRecEquitySharePct: number | null;
    maxRecEquitySharePct: number | null;
  } {
    const overrideMaxEquityStr = p("overrideMaxEquityPct");
    const maxEquitySharePct =
      overrideMaxEquityStr !== ""
        ? parseFloat(overrideMaxEquityStr)
        : profileNum(profile, "contact.profiling.maxStockShare") ?? null;

    const overrideMinRecStr = p("overrideMinRecEquityPct");
    const minRecEquitySharePct =
      overrideMinRecStr !== ""
        ? parseFloat(overrideMinRecStr)
        : profileNum(profile, "contact.profiling.recMinStockShare") ?? null;

    const overrideMaxRecStr = p("overrideMaxRecEquityPct");
    const maxRecEquitySharePct =
      overrideMaxRecStr !== ""
        ? parseFloat(overrideMaxRecStr)
        : profileNum(profile, "contact.profiling.recMaxStockShare") ?? null;

    return { maxEquitySharePct, minRecEquitySharePct, maxRecEquitySharePct };
  }

  // =========================================================================
  // PRIVATE: Equity Within / Under / Over flags and band text strings
  //
  // Priority:
  //   1. Test-parameter overrides (overrideEquityWithin / overrideRecEquityBandText)
  //   2. Derived from profile rec-band percentages (recMinStockShare / recMaxStockShare)
  //   3. null — when no rec-band data is available
  //
  // NOTE: Automatic derivation from finalRisk tolerance label is intentionally
  // deferred — implement a resolveRiskBands() helper when ready.
  // =========================================================================
  private resolveEquityBands(
    p: PFn,
    finStockPart: number | null,
    minRecEquitySharePct: number | null,
    maxRecEquitySharePct: number | null,
    maxEquitySharePct: number | null
  ): {
    resolvedIsWithin: boolean | null;
    resolvedIsUnder: boolean | null;
    resolvedIsOver: boolean | null;
    resolvedBandText: string | null;
    resolvedMaxText: string | null;
    recEquityBandText: string | null;
    maxEquityText: string | null;
  } {
    // Test-parameter overrides
    const overrideWithinStr = p("overrideEquityWithin");
    const overrideUnderStr = p("overrideEquityUnder");
    const overrideOverStr = p("overrideEquityOver");
    const overrideBandStr = p("overrideRecEquityBandText");

    let resolvedIsWithin: boolean | null =
      overrideWithinStr !== "" ? overrideWithinStr === "true" : null;
    let resolvedIsUnder: boolean | null =
      overrideUnderStr !== "" ? overrideUnderStr === "true" : null;
    let resolvedIsOver: boolean | null =
      overrideOverStr !== "" ? overrideOverStr === "true" : null;
    let resolvedBandText: string | null =
      overrideBandStr !== "" ? overrideBandStr : null;
    const resolvedMaxText: string | null =
      maxEquitySharePct !== null ? `${Math.round(maxEquitySharePct)} %` : null;

    // Fallback: derive from profile rec-band percentages
    if (
      resolvedIsWithin === null &&
      finStockPart !== null &&
      minRecEquitySharePct !== null &&
      maxRecEquitySharePct !== null
    ) {
      const sp = parseFloat((finStockPart * 100).toFixed(2));
      const recMin = parseFloat(minRecEquitySharePct.toFixed(2));
      const recMax = parseFloat(maxRecEquitySharePct.toFixed(2));
      resolvedIsWithin = sp >= recMin && sp <= recMax;
      resolvedIsUnder = sp < recMin;
      resolvedIsOver = sp > recMax;
      if (!resolvedBandText) {
        resolvedBandText =
          minRecEquitySharePct === 0
            ? `${Math.round(maxRecEquitySharePct)} %`
            : `${Math.round(minRecEquitySharePct)}-${Math.round(
                maxRecEquitySharePct
              )} %`;
      }
    }

    return {
      resolvedIsWithin,
      resolvedIsUnder,
      resolvedIsOver,
      resolvedBandText,
      resolvedMaxText,
      recEquityBandText: resolvedBandText,
      maxEquityText: resolvedMaxText,
    };
  }

  // =========================================================================
  // PRIVATE: BLK_CONCL_SCOPE — always generated, lists factors considered
  // =========================================================================
  private buildConclScope(
    p: PFn,
    knowAgg: string | null,
    expAgg: string | null,
    finalRisk: string | null
  ): string {
    const _horizonPriv = p("mifidEarliestWithdrawal");
    const _horizonOrg = p("mifidInvestmentHorizon");
    const tidlig =
      (_horizonPriv
        ? INVESTMENT_HORIZON_LABELS[_horizonPriv]
        : ORG_INVESTMENT_HORIZON_LABELS[_horizonOrg]) || "\u2013";
    const hoved =
      INVESTMENT_GOAL_LABELS[
        p("mifidMainInvestmentPurpose") || p("mifidInvestmentGoal")
      ] || "\u2013";
    const risk = finalRisk || "\u2013";
    const fall =
      VALUE_DECLINE_IMPACT_LABELS[p("mifidValueDeclineImpact")] || "\u2013";
    const liq = LIQUIDITY_NEED_LABELS[p("mifidLiquidityNeed")] || "\u2013";
    const kn = knowAgg || "\u2013";
    const ex = expAgg || "\u2013";
    return `Løsningen er vurdert opp mot kundens investeringshorisont (${tidlig}), investeringsmål (${hoved}), risikotoleranse (${risk}), evne til å bære tap (${fall}), likviditetsbehov (${liq}) og oppgitt kunnskap og erfaring knyttet til relevante investeringstyper (${kn} / ${ex}).`;
  }

  // =========================================================================
  // PRIVATE: BLK_CONCL_* — conclusion block paragraphs
  //
  // Three variants share a preamble sentence, then append a conclusion
  // about the equity position relative to the recommended band:
  //   WITHIN — equity is within the band → suitable
  //   UNDER  — equity is below the band  → suitable but lower return potential
  //   OVER   — equity is above the band  → not suitable
  // =========================================================================
  private buildConclTexts(
    finStockPart: number | null,
    finBondPart: number | null,
    investmentAmount: number | null,
    resolvedIsWithin: boolean | null,
    resolvedIsUnder: boolean | null,
    resolvedIsOver: boolean | null,
    resolvedBandText: string | null,
    resolvedMaxText: string | null,
    p: PFn,
    knowAgg: string | null,
    expAgg: string | null,
    finalRisk: string | null
  ): Pick<
    SuitStatementData,
    | "BLK_CONCL_WITHIN"
    | "BLK_CONCL_UNDER"
    | "BLK_CONCL_OVER"
    | "BLK_CONCL_SCOPE"
  > {
    const scope = this.buildConclScope(p, knowAgg, expAgg, finalRisk);

    if (finStockPart === null || finBondPart === null) {
      return {
        BLK_CONCL_WITHIN: null,
        BLK_CONCL_UNDER: null,
        BLK_CONCL_OVER: null,
        BLK_CONCL_SCOPE: scope,
      };
    }

    const stockPct = Math.round(finStockPart * 100);
    const bondPct = Math.round(finBondPart * 100);
    const amountStr =
      investmentAmount != null ? formatNOK(investmentAmount) : null;
    const bandPrefix =
      resolvedBandText && resolvedMaxText
        ? `Investorprofilen tilsier anbefalt aksjeandel ${resolvedBandText} og maksimal aksjeandel ${resolvedMaxText}. `
        : null;

    // Preamble sentence shared by WITHIN, UNDER and OVER variants
    const preamble = (egnet: boolean): string => {
      const suitText = egnet ? "egnet" : "ikke egnet";
      return amountStr
        ? `Basert på innhentede kundeopplysninger og beregnet investorprofil vurderes anbefalt investering som ${suitText} for kunden. Anbefalingen gjelder investering av ${amountStr} i en portefølje med ${stockPct} % aksjeeksponering og ${bondPct} % renteeksponering.`
        : `Basert på innhentede kundeopplysninger og beregnet investorprofil vurderes anbefalt investering som ${suitText} for kunden. Porteføljen har ${stockPct} % aksjeeksponering og ${bondPct} % renteeksponering.`;
    };

    return {
      BLK_CONCL_WITHIN:
        resolvedIsWithin === true && bandPrefix
          ? `${preamble(
              true
            )}\n\n${bandPrefix}Aksjeandelen etter gjennomføring (${stockPct} %) ligger innenfor kundens anbefalte aksjeintervall og under maksimal aksjeandel.`
          : null,

      BLK_CONCL_UNDER:
        resolvedIsUnder === true && bandPrefix
          ? `${preamble(
              true
            )}\n\n${bandPrefix}Aksjeandelen etter gjennomføring (${stockPct} %) er lavere enn kundens anbefalte aksjeintervall. Løsningen vurderes likevel egnet, men forventet langsiktig avkastningspotensial kan bli lavere enn ved en portefølje innenfor anbefalt aksjeandel. Begrunnelse fremgår av rådgivers kommentar.`
          : null,

      BLK_CONCL_OVER:
        resolvedIsOver === true && bandPrefix
          ? `${preamble(
              false
            )}\n\n${bandPrefix}Aksjeandelen etter gjennomføring (${stockPct} %) overstiger kundens anbefalte risikoramme. Begrunnelse fremgår av rådgivers kommentar.`
          : null,

      BLK_CONCL_SCOPE: scope,
    };
  }

  // =========================================================================
  // PRIVATE: BLK_ESG_* — ESG preference paragraphs
  //
  // Two variants based on whether the customer has ESG preferences or not:
  //   YES   — customer has ESG preferences → explain that they were considered
  //   NONE  — customer has no ESG preferences → explain that none were considered
  // =========================================================================
  private buildEsgTexts(
    esg: string | null,
    esgText: string | null
  ): Pick<
    SuitStatementData,
    "BLK_ESG_PREFERENCE" | "BLK_ESG_YES" | "BLK_ESG_NONE" | "ESG"
  > {
    return {
      ESG: ["YES", "JA"].includes(esg?.toUpperCase() || "") ? "YES" : "NO",
      BLK_ESG_PREFERENCE: esgText
        ? `Kundens b\u00e6rekraftspreferanse er registrert som \u00ab${esgText}\u00bb`
        : null,
      BLK_ESG_YES: ["YES", "JA"].includes(esg?.toUpperCase() || "")
        ? "Kunden har opplyst at det foreligger egne bærekraftpreferanser. Vurdering av hvordan disse preferansene er hensyntatt i anbefalingen fremgår av rådgivers kommentar. "
        : null,
      BLK_ESG_NONE: ["NO", "NEI"].includes(esg?.toUpperCase() || "")
        ? "Kunden har opplyst at det ikke foreligger egne bærekraftpreferanser som skal legges til grunn ved valg av finansielle instrumenter. Bærekraftpreferanser har derfor ikke vært et separat styrende egnethetskriterium i utvelgelsen av anbefalt portefølje."
        : null,
    };
  }

  // =========================================================================
  // PRIVATE: BLK_KNOWEXP_* — knowledge and experience paragraphs
  //
  // Two variants based on aggregated knowledge and experience levels:
  //   BASE — general statement about how knowledge/experience was assessed
  //   LOW  — customer has low knowledge/experience → add warning that this was an important consideration in the recommendation
  // =========================================================================
  // =========================================================================
  // PRIVATE: BLK_KNOWEXP_* — knowledge and experience paragraphs
  //
  // BASE is always shown. LOW is shown when the customer's knowledge or
  // experience is low for instrument types that are present in the portfolio.
  //
  // «Relevant» instrument types are inferred from finStockPart / finBondPart:
  //   finStockPart > 0  → listed and unlisted equity knowledge matters
  //   finBondPart  > 0  → fund and bond knowledge matters
  //   complex           → always included
  //
  // NOTE: The relevance logic is deliberately simple. Refine by passing richer
  // order data (e.g. buyOrders with isStock, instrument category) when ready.
  // =========================================================================
  private buildKnowExpTexts(
    knowAgg: string | null,
    expAgg: string | null,
    scores: {
      know_funds: string;
      know_listed_eq: string;
      know_unlisted_eq: string;
      know_bonds: string;
      know_complex: string;
      exp_funds: string;
      exp_listed_eq: string;
      exp_unlisted_eq: string;
      exp_bonds: string;
      exp_complex: string;
    },
    finStockPart: number | null,
    finBondPart: number | null
  ): Pick<SuitStatementData, "BLK_KNOWEXP_BASE" | "BLK_KNOWEXP_LOW"> {
    // Determine which instrument types are present in the portfolio
    const hasEquity = finStockPart !== null && finStockPart > 0;
    const hasBondOrFund = finBondPart !== null && finBondPart > 0;

    // Collect knowledge scores for relevant instrument types
    const relevantKnow: string[] = [];
    const relevantExp: string[] = [];
    if (hasEquity) {
      relevantKnow.push(scores.know_listed_eq, scores.know_unlisted_eq);
      relevantExp.push(scores.exp_listed_eq, scores.exp_unlisted_eq);
    }
    if (hasBondOrFund) {
      relevantKnow.push(scores.know_funds, scores.know_bonds);
      relevantExp.push(scores.exp_funds, scores.exp_bonds);
    }
    // Complex instruments are always checked
    relevantKnow.push(scores.know_complex);
    relevantExp.push(scores.exp_complex);

    // Low = aggregate is "Lav" / "Ingen", or any relevant score is "1" (lowest)
    const isLowKnowledge =
      knowAgg === "Lav" ||
      expAgg === "Ingen" ||
      relevantKnow.some((s) => s === "1") ||
      relevantExp.some((s) => s === "1");

    return {
      BLK_KNOWEXP_BASE:
        "Kundens kunnskap og erfaring er vurdert opp mot relevante investeringstyper. ",
      BLK_KNOWEXP_LOW: isLowKnowledge
        ? "Kunden har oppgitt lav kunnskap og/eller erfaring knyttet til relevante investeringstyper, og dette er derfor et viktig hensyn i valg av anbefalt portefølje. "
        : null,
    };
  }

  // =========================================================================
  // PRIVATE: BLK_LIQ_* — liquidity paragraphs
  //
  // Three variants based on liquidity class and mismatch flag:
  //   LIQUID           — portfolio is mainly liquid → describe liquidity and settlement times
  //   PARTLY_ILLIQUID  — portfolio has some illiquid assets → describe liquidity, settlement times and illiquid part
  //   LIQUIDITY_MISMATCH — customer's stated liquidity need is high but portfolio has illiquid assets → add warning about potential mismatch
  // =========================================================================
  private buildLiquidityTexts(
    liquidityClass: "LIQUID" | "PARTLY_ILLIQUID" | null,
    liquidityMismatch: boolean | null,
    settlementsDaysText: string | null,
    illiquidPartTimeText: string | null,
    withdraw: string | null
  ): Pick<
    SuitStatementData,
    "BLK_LIQ_LIQUID" | "BLK_LIQ_PARTLY_ILLIQUID" | "BLK_LIQ_WARN"
  > {
    return {
      BLK_LIQ_LIQUID:
        liquidityClass === "LIQUID"
          ? `Porteføljen består hovedsakelig av instrumenter som normalt kan realiseres med oppgjør innen ${settlementsDaysText} under normale markedsforhold. Dette er vurdert opp mot kundens oppgitte likviditetsbehov (${withdraw})`
          : null,
      BLK_LIQ_PARTLY_ILLIQUID:
        liquidityClass === "PARTLY_ILLIQUID"
          ? `Hoveddelen av porteføljen kan normalt realiseres med oppgjør innen ${settlementsDaysText} under normale markedsforhold. En mindre del kan ha lengre realiseringstid (${illiquidPartTimeText}), og realisering kan ta lengre tid i krevende markedsforhold. Dette er vurdert opp mot kundens oppgitte likviditetsbehov (${withdraw}). `
          : null,
      BLK_LIQ_WARN: liquidityMismatch
        ? "Kunden har oppgitt høyt likviditetsbehov. Eventuelle mindre likvide innslag krever særskilt vurdering og begrunnelse. "
        : null,
    };
  }

  // =========================================================================
  // PRIVATE: BLK_RISK_NOTE_* — risk warning notes
  //
  // BLK_RISK_NOTE_CREDIT — shown when CreditRiskFlag is true; informs that the
  // fixed-income portion may contain corporate/high-yield bonds and is therefore
  // exposed to credit risk beyond pure interest-rate movements.
  // =========================================================================
  private buildRiskNoteTexts(
    creditRiskFlag: boolean
  ): Pick<SuitStatementData, "BLK_RISK_NOTE_CREDIT"> {
    return {
      BLK_RISK_NOTE_CREDIT: creditRiskFlag
        ? "Rentedelen består av rentebærende investeringer som kan inneholde kredittrisiko, herunder innslag av foretaksobligasjoner og/eller høyrente, og kan derfor gi verdiendringer utover det som følger av rentenivå alene."
        : null,
    };
  }

  // =========================================================================
  // PRIVATE: BLK_EQUITY_* — short equity justification sentences
  //
  // One-sentence descriptions of the equity position vs. recommended band.
  //   WITHIN — equity is within band → compliant with risk tolerance and goals
  //   UNDER  — equity is below band  → lower return potential; suitable nonetheless
  //   OVER   — equity is above band  → higher risk than recommended
  // =========================================================================
  private buildEquityTexts(
    p: PFn,
    finStockPart: number | null,
    resolvedIsWithin: boolean | null,
    resolvedIsUnder: boolean | null,
    resolvedIsOver: boolean | null,
    resolvedBandText: string | null,
    resolvedMaxText: string | null
  ): Pick<
    SuitStatementData,
    "BLK_EQUITY_WITHIN" | "BLK_EQUITY_UNDER" | "BLK_EQUITY_OVER"
  > {
    if (finStockPart === null) {
      return {
        BLK_EQUITY_WITHIN: null,
        BLK_EQUITY_UNDER: null,
        BLK_EQUITY_OVER: null,
      };
    }

    const stockPct = Math.round(finStockPart * 100);

    return {
      BLK_EQUITY_WITHIN:
        resolvedIsWithin === true && resolvedBandText && resolvedMaxText
          ? `Porteføljens aksjeandel på ${stockPct} % ligger innenfor kundens anbefalte aksjeintervall (${resolvedBandText}) og under kundens maksimale aksjeandel (${resolvedMaxText}). Løsningen vurderes derfor å være i samsvar med kundens risikotoleranse (${
              p("mifidRiskToleranceVolatility") || "risikotoleranse"
            }) og mål (${
              INVESTMENT_GOAL_LABELS[
                p("mifidMainInvestmentPurpose") || p("mifidInvestmentGoal")
              ] || "mål"
            }).`
          : null,

      BLK_EQUITY_UNDER:
        resolvedIsUnder === true && resolvedBandText
          ? `Porteføljens aksjeandel på ${stockPct} % er lavere enn kundens anbefalte aksjeintervall (${resolvedBandText}). Løsningen kan innebære lavere forventet langsiktig avkastningspotensial enn ved anbefalt aksjeandel, men vurderes forenlig med kundens oppgitte mål og øvrige forhold. Begrunnelse fremgår av rådgivers kommentar.`
          : null,

      BLK_EQUITY_OVER:
        resolvedIsOver === true && resolvedBandText && resolvedMaxText
          ? `Porteføljens aksjeandel på ${stockPct} % overstiger kundens anbefalte risikoramme (${resolvedBandText} / maks ${resolvedMaxText}) og kan innebære høyere risiko enn kunden er anbefalt å ta. Begrunnelse fremgår av rådgivers kommentar.`
          : null,
    };
  }

  // =========================================================================
  // PUBLIC: Main entry point — orchestrates all private methods
  // =========================================================================
  compute(
    contact: Contact,
    profile: PMap,
    orders: SuitOrderInput[] = [],
    stockPartOverride?: StockPartOverride
  ): SuitStatementData {
    const p = (key: string) => pv(profile, key);

    // -- Required fields --------------------------------------------------
    const requiredKeys = [
      "mifidRiskToleranceVolatility",
      "mifidMarketDropResponse",
      "mifidLiquidityNeed",
      "mifidValueDeclineImpact",
      "mifidKnowledgeOfMutualFunds",
      "mifidExperienceOfMutualFunds",
      "mifidKnowledgeOfListedStocks",
      "mifidExperienceOfListedStocks",
      "mifidKnowledgeOfUnlistedStocks",
      "mifidExperienceOfUnlistedStocks",
      "mifidKnowledgeOfBonds",
      "mifidExperienceOfBonds",
      "mifidKnowledgeOfComplexInvestments",
      "mifidExperienceOfComplexInvestments",
      "mifidRelevantExperienceEducation",
      "mifidEsgPreference",
    ];
    const dataComplete =
      requiredKeys.every((k) => p(k) !== "") &&
      (p("mifidEarliestWithdrawal") !== "" ||
        p("mifidInvestmentHorizon") !== "") &&
      (p("mifidMainInvestmentPurpose") !== "" ||
        p("mifidInvestmentGoal") !== "");

    // -- Risk tolerance ---------------------------------------------------
    const finalRisk = resolveRiskTolerance(
      p("mifidRiskToleranceVolatility"),
      p("mifidMarketDropResponse")
    );

    // -- Knowledge / experience aggregates --------------------------------
    const knowAgg = (() => {
      const avg = numericAvg([
        p("mifidKnowledgeOfMutualFunds"),
        p("mifidKnowledgeOfListedStocks"),
        p("mifidKnowledgeOfUnlistedStocks"),
        p("mifidKnowledgeOfBonds"),
        p("mifidKnowledgeOfComplexInvestments"),
      ]);
      return avg !== null ? aggregateKnowledge(avg) : null;
    })();
    const expAgg = (() => {
      const avg = numericAvg([
        p("mifidExperienceOfMutualFunds"),
        p("mifidExperienceOfListedStocks"),
        p("mifidExperienceOfUnlistedStocks"),
        p("mifidExperienceOfBonds"),
        p("mifidExperienceOfComplexInvestments"),
      ]);
      return avg !== null ? aggregateExperience(avg) : null;
    })();

    // -- Finance background -----------------------------------------------
    const finBgRaw = p("mifidRelevantExperienceEducation");
    const finBgFlag = finBgRaw === "" ? null : finBgRaw === "1" ? "Nei" : "Ja";
    const finBgText =
      finBgRaw === ""
        ? null
        : finBgRaw === "1"
        ? "Ingen relevant erfaring eller utdanning"
        : finBgRaw === "2"
        ? "Noe erfaring fra finansielle markeder"
        : finBgRaw === "3"
        ? "Relevant utdanning innen finans/økonomi"
        : finBgRaw === "4"
        ? "Yrkeserfaring fra finansbransjen"
        : null;

    // -- ESG --------------------------------------------------------------
    const esgRaw = p("mifidEsgPreference");
    const esgPrefType = p("mifidEsgPreferenceType");
    // ESGText: shortened version for natural sentence formatting
    let esgText: string | null = null;
    if (esgRaw === "Nei") {
      esgText = ESG_TEXT_VARIANTS["Nei"];
    } else if (esgRaw === "Ja" && esgPrefType) {
      esgText =
        ESG_TEXT_VARIANTS[esgPrefType] ??
        ESG_PREF_TYPE_LABELS[esgPrefType] ??
        esgPrefType;
    }
    // ESGType: aggregate indicator of whether ESG is opplyst (informed) or not
    const esgType =
      esgRaw === "Ja" ? "Opplyst" : esgRaw === "Nei" ? "Ingen" : null;

    // -- Customer classification ------------------------------------------
    const classCode = contact.classification?.code ?? "";
    const clientClassText =
      classCode === "NPROF"
        ? "Ikke-profesjonell"
        : classCode === "PROF"
        ? "Profesjonell"
        : classCode === "NCLAS"
        ? "Ikke-klassifisert"
        : classCode !== ""
        ? classCode
        : null;

    // -- Investment amount ------------------------------------------------
    // NOTE: MaxEquityBandText / MaxEquityText from finalRisk are deferred.
    const investmentAmount =
      stockPartOverride?.investmentAmount != null
        ? stockPartOverride.investmentAmount
        : orders.length > 0
        ? orders.reduce((s, o) => s + o.amountToInvest + o.oneOffAmount, 0)
        : 0;

    // -- Portfolio equity fractions (private method) ----------------------
    const { initFraction, finStockPart, finBondPart } = this.computeEquityParts(
      p,
      profile,
      orders,
      stockPartOverride
    );

    // -- Equity share pct fields (private method) -------------------------
    const { maxEquitySharePct, minRecEquitySharePct, maxRecEquitySharePct } =
      this.resolveEquitySharePcts(p, profile);

    // -- Equity flags + band text (private method) ------------------------
    const {
      resolvedIsWithin,
      resolvedIsUnder,
      resolvedIsOver,
      resolvedBandText,
      resolvedMaxText,
      recEquityBandText,
      maxEquityText,
    } = this.resolveEquityBands(
      p,
      finStockPart,
      minRecEquitySharePct,
      maxRecEquitySharePct,
      maxEquitySharePct
    );

    // -- Block texts (private methods) ------------------------------------
    const conclTexts = this.buildConclTexts(
      finStockPart,
      finBondPart,
      investmentAmount,
      resolvedIsWithin,
      resolvedIsUnder,
      resolvedIsOver,
      resolvedBandText,
      resolvedMaxText,
      p,
      knowAgg,
      expAgg,
      finalRisk
    );
    const equityTexts = this.buildEquityTexts(
      p,
      finStockPart,
      resolvedIsWithin,
      resolvedIsUnder,
      resolvedIsOver,
      resolvedBandText,
      resolvedMaxText
    );

    const esgTexts = this.buildEsgTexts(esgRaw, esgText);
    const liquidityTexts = this.buildLiquidityTexts(
      p("mifidLiquidityClass") as "LIQUID" | "PARTLY_ILLIQUID" | null,
      p("mifidLiquidityMismatch") === "true",
      p("mifidSettlementsDaysText") || null,
      p("mifidIlliquidPartTimeText") || null,
      p("mifidLiquidityNeed") || null
    );
    const riskNoteTexts = this.buildRiskNoteTexts(
      p("CreditRiskFlag") === "true"
    );
    const knowExpTexts = this.buildKnowExpTexts(
      knowAgg,
      expAgg,
      {
        know_funds: p("mifidKnowledgeOfMutualFunds"),
        know_listed_eq: p("mifidKnowledgeOfListedStocks"),
        know_unlisted_eq: p("mifidKnowledgeOfUnlistedStocks"),
        know_bonds: p("mifidKnowledgeOfBonds"),
        know_complex: p("mifidKnowledgeOfComplexInvestments"),
        exp_funds: p("mifidExperienceOfMutualFunds"),
        exp_listed_eq: p("mifidExperienceOfListedStocks"),
        exp_unlisted_eq: p("mifidExperienceOfUnlistedStocks"),
        exp_bonds: p("mifidExperienceOfBonds"),
        exp_complex: p("mifidExperienceOfComplexInvestments"),
      },
      finStockPart,
      finBondPart
    );

    return {
      // Document references (sourced externally)
      MIFIDProfileDocRef: null,
      InvestorProfileDocRef: null,
      OrderDocRef: null,

      // Customer
      ClientName: contact.name ?? null,
      ClientClassText: clientClassText,
      tidligsteUttak: (() => {
        const priv = p("mifidEarliestWithdrawal");
        const org = p("mifidInvestmentHorizon");
        return priv
          ? INVESTMENT_HORIZON_LABELS[priv] ?? priv
          : org
          ? ORG_INVESTMENT_HORIZON_LABELS[org] ?? org
          : null;
      })(),
      hovedmal: (() => {
        const raw = p("mifidMainInvestmentPurpose") || p("mifidInvestmentGoal");
        return raw ? INVESTMENT_GOAL_LABELS[raw] ?? raw : null;
      })(),
      riskkomfort: finalRisk,
      withdraw:
        LIQUIDITY_NEED_LABELS[p("mifidLiquidityNeed")] ??
        (p("mifidLiquidityNeed") || null),
      fallcons:
        VALUE_DECLINE_IMPACT_LABELS[p("mifidValueDeclineImpact")] ??
        (p("mifidValueDeclineImpact") || null),

      // Instruments — knowledge / experience / frequency / period
      know_funds: p("mifidKnowledgeOfMutualFunds") || null,
      exp_funds: p("mifidExperienceOfMutualFunds") || null,
      freq_funds: p("mifidTradingFrequencyOfMutualFunds") || null,
      period_funds: p("mifidPeriodOfMutualFunds") || null,
      know_listed_eq: p("mifidKnowledgeOfListedStocks") || null,
      exp_listed_eq: p("mifidExperienceOfListedStocks") || null,
      freq_listed_eq: p("mifidTradingFrequencyOfListedStocks") || null,
      period_listed_eq: p("mifidPeriodOfListedStocks") || null,
      know_unlisted_eq: p("mifidKnowledgeOfUnlistedStocks") || null,
      exp_unlisted_eq: p("mifidExperienceOfUnlistedStocks") || null,
      freq_unlisted_eq: p("mifidTradingFrequencyOfUnlistedStocks") || null,
      period_unlisted_eq: p("mifidPeriodOfUnlistedStocks") || null,
      know_bonds: p("mifidKnowledgeOfBonds") || null,
      exp_bonds: p("mifidExperienceOfBonds") || null,
      freq_bonds: p("mifidTradingFrequencyOfBonds") || null,
      period_bonds: p("mifidPeriodOfBonds") || null,
      know_complex: p("mifidKnowledgeOfComplexInvestments") || null,
      exp_complex: p("mifidExperienceOfComplexInvestments") || null,
      freq_complex: p("mifidTradingFrequencyOfComplexInvestments") || null,
      period_complex: p("mifidPeriodOfComplexInvestments") || null,
      finance_background_flag: finBgFlag,
      finance_background_text: finBgText,

      // Financial bands
      NetWorthBandText: p("mifidTotalNetWealth") || null,
      LiquidFundsBandText: p("mifidLiquidInvestableWealth") || null,
      GrossIncomeBandText: p("mifidGrossAnnualIncome") || null,
      LiquidityBufferBandText: p("mifidLiquidityBuffer") || null,
      DebtBurdenText: p("mifidDebtBurden") || null,
      IncomeStabilityText:
        INCOME_STABILITY_LABELS[p("mifidIncomeStability")] ??
        (p("mifidIncomeStability") || null),

      // ESG
      ESGText: esgText,
      ESGType: esgType,
      ESGPreferenceType: esgPrefType || null,
      ESGPreferenceText: esgPrefType
        ? ESG_PREF_TYPE_LABELS[esgPrefType] ?? esgPrefType
        : null,

      // Equity bands
      RecEquityBandText: recEquityBandText,
      MaxEquityBandText: null, // TODO: derive from finalRisk when ready
      MaxEquityText: maxEquityText,
      currentEquitySharePct: initFraction !== null ? initFraction * 100 : null,
      maxEquitySharePct,
      minRecEquitySharePct,
      maxRecEquitySharePct,

      // Aggregates
      know: knowAgg,
      exp: expAgg,

      // Portfolio
      investmentAmount,
      port_stock_pct: finStockPart,
      orderStockAllocation: null,
      port_bond_pct: finBondPart,
      LockupText: null,
      AllocationListText: null,
      CreditRiskFlag: null,
      Portfolio_SRRI: null,
      Portfolio_MRHPText: null,
      LiquidityClass: null,
      SettlementsDaysText: null,
      IlliquidPartTimeText: null,
      EntryFeePct: null,
      MgmtFeePct: null,
      PerfFeePct: null,
      OngoingCostsPct: null,

      // Metadata
      AdvisorComment: null,
      AdvisorName: null,
      GeneratedDateTime: new Date().toISOString(),

      // Flags
      EquityWithin: resolvedIsWithin,
      EquityUnder: resolvedIsUnder,
      EquityOver: resolvedIsOver,
      Suitable: null, // TODO: derive when ready
      FinancialBandsAvailable: null,
      Portfolio_SRRI_Available: null,
      MRHPMismatch: null,
      LiquidityMismatch: null,
      AllocationListAvailable: null,
      DataCompletenessFlag: dataComplete,
      InvestmentvsNetWorthFlag: null,

      // Block texts (generated by private methods above)
      ...conclTexts,
      ...equityTexts,
      ...esgTexts,
      ...liquidityTexts,
      ...riskNoteTexts,
      ...knowExpTexts,
    };
  }
}
